import { useEffect, useRef } from 'react';
import type { AppStore } from '../stores/createStore';
import { hydrate as hydrateAuth, setCredentials, logout, type User } from '../stores/slices/authSlice';
import { useAppSelector } from '../stores/hooks';

/**
 * tRPC React hooks 类型
 */
interface TRPCReact {
  auth: {
    me: {
      useQuery: (input: undefined, options?: { enabled?: boolean; retry?: boolean }) => {
        data: any;
        error: any;
      };
    };
  };
}

/**
 * AuthProvider 配置
 */
export interface AuthProviderProps {
  store: AppStore;
  trpc: TRPCReact;
  /** 公开路径：在此类路径下不请求 auth.me，避免未登录时触发 UNAUTHORIZED 及错误提示（如开放活动页） */
  publicPaths?: string[];
}

/**
 * 认证服务组件
 * 
 * 职责：
 * 1. 当有 token 但没有用户信息时，自动获取用户信息
 * 2. 监听 localStorage 变化，同步跨标签页的认证状态
 * 
 * 使用方式：在 Providers 中全局挂载，无需子组件
 */
export function AuthProvider({ store, trpc, publicPaths }: AuthProviderProps) {
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const token = useAppSelector((state) => state.auth.token);
  const user = useAppSelector((state) => state.auth.user);
  const isHydrated = useAppSelector((state) => state.auth.isHydrated);

  const hasFetchedRef = useRef(false);
  const isProcessingRef = useRef(false);
  const isLoggingOutRef = useRef(false);
  const previousUserRef = useRef<User | null>(null); // 记录之前的 user 状态，用于区分初始状态和退出登录

  // ===== 功能 1: 自动获取用户信息 =====
  // Web 端（Cookie 认证）：当页面加载时，即使没有 token，也应该检查 Cookie 并获取用户信息
  // 移动端（Header 认证）：当有 token 但没有用户信息时，获取用户信息
  const isWeb = typeof window !== 'undefined' &&
    !/ReactNative/i.test(window.navigator.userAgent);

  // ✅ 公开路径（如开放活动页）：不请求 auth.me，避免未登录时触发 UNAUTHORIZED 及错误提示
  const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';
  const isPublicPath = Array.isArray(publicPaths) && publicPaths.some(
    (p) => currentPath === p || currentPath.startsWith(p + '/')
  );

  // Web 端：页面加载后检查 Cookie（即使 isAuthenticated 为 false）
  // 移动端：只有当有 token 且已认证时才获取用户信息
  // ✅ 登录页检查 Cookie 是正常的，如果没有 Cookie 就说明未登录，不应该触发全局错误处理
  // ✅ 退出登录时，不尝试获取用户信息（避免触发 UNAUTHORIZED 错误）
  const shouldFetchUser = !isPublicPath && isHydrated && !user && !hasFetchedRef.current && !isLoggingOutRef.current && (
    isWeb
      ? true // ✅ Web 端：总是尝试检查 Cookie（即使 isAuthenticated 为 false）
      : (isAuthenticated && !!token) // 移动端：需要 token 和 isAuthenticated
  );

  const { data: userData, error: userError } = trpc.auth.me.useQuery(undefined, {
    enabled: shouldFetchUser,
    retry: false,
  });

  // 获取用户信息成功，更新 Redux store
  useEffect(() => {
    if (hasFetchedRef.current) return;

    if (userData && !user) {
      hasFetchedRef.current = true;
      // ✅ 直接使用 tRPC 返回的数据，类型自动推断
      // Web 端：token 为 null（使用 Cookie），移动端：使用传入的 token
      store.dispatch(
        setCredentials({
          user: {
            id: userData.id,
            username: userData.username,
            nickname: userData.nickname ?? undefined,
            email: userData.email ?? undefined,
            mobile: userData.mobile ?? undefined,
            avatarUrl: userData.avatarUrl ?? undefined,
            status: userData.status ?? undefined,
            type: (userData as any).type ?? undefined,
            tenantId: typeof userData.tenantId === 'string' ? userData.tenantId : undefined,
            permissions: (userData as any).permissions ?? undefined,
            roles: (userData as any).roles ?? undefined,
            apps: (userData as any).apps ?? undefined,
            spaces: (userData as any).spaces ?? undefined,
          },
          token: token || undefined, // Web 端为 undefined，移动端使用 token
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData]);

  // 获取用户信息失败，标记已获取（避免重复请求）
  useEffect(() => {
    if (userError && !user && !hasFetchedRef.current) {
      hasFetchedRef.current = true;
      console.warn('获取用户信息失败:', userError.message);
    }
  }, [userError, user]);

  // ===== 功能 2: 监听退出登录状态 =====
  // 当 Redux 状态变为未登录时，标记为退出登录状态，避免触发 auth.me 请求
  // ✅ 修复：区分"页面刷新时的初始状态"和"真正的退出登录"
  // - 初始状态：isHydrated 刚变为 true，user 为 null，previousUserRef 也为 null
  // - 退出登录：user 从有值变为 null（previousUserRef 有值）
  useEffect(() => {
    // 只有在 hydration 完成后才处理退出登录逻辑
    if (!isHydrated) {
      return;
    }

    if (!isAuthenticated && !user) {
      // ✅ 关键修复：只有当之前有 user（说明是从已登录变为未登录）时，才认为是退出登录
      // 如果 previousUserRef 为 null，说明是页面刷新时的初始状态，不应该阻止 Cookie 检查
      if (previousUserRef.current !== null) {
        // 从已登录变为未登录，这是真正的退出登录
        // 设置标志，避免立即尝试获取用户信息
        isLoggingOutRef.current = true;
        hasFetchedRef.current = true; // 标记已获取，避免重复请求
        
        // 延迟重置标志，允许下次登录后正常获取用户信息
        const timer = setTimeout(() => {
          isLoggingOutRef.current = false;
          hasFetchedRef.current = false;
        }, 1000);
        
        return () => clearTimeout(timer);
      }
      // 如果 previousUserRef 为 null，说明是初始状态，不阻止 Cookie 检查
    } else if (isAuthenticated && user) {
      // 已登录，重置退出登录标志
      isLoggingOutRef.current = false;
    }

    // 更新 previousUserRef，用于下次判断
    previousUserRef.current = user;
  }, [isAuthenticated, user, isHydrated]);

  // ===== 功能 3: 跨标签页同步 =====
  // 监听 localStorage 变化，同步其他标签页的登录/登出操作
  useEffect(() => {
    if (!isHydrated) return;

    const handleStorageChange = (e: StorageEvent) => {
      if (isProcessingRef.current) return;
      if (e.key !== 'token' && e.key !== 'refreshToken') return;

      isProcessingRef.current = true;

      const newToken = localStorage.getItem('token');
      const state = store.getState();

      // 其他标签页登出了
      if (!newToken && state.auth.token) {
        store.dispatch(logout());
        hasFetchedRef.current = false; // 重置，允许下次登录后获取用户信息
        isLoggingOutRef.current = true; // 标记为退出登录状态
      }
      // 其他标签页登录了或 token 变化了
      else if (newToken && newToken !== state.auth.token) {
        store.dispatch(hydrateAuth());
        hasFetchedRef.current = false; // 重置，允许获取新用户信息
        isLoggingOutRef.current = false; // 重置退出登录标志
      }

      isProcessingRef.current = false;
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [isHydrated, store]);

  return null;
}

